import { useCallback, useEffect, useRef, useState } from "react";
import type { JSX } from "react";
import { formatIngredientDisplay } from "../../shared/quantity";
import { sortStepsForCookMode } from "../../shared/recipe-steps";
import type { CookSession, Recipe, StepTimerDef } from "../../shared/recipes";
import { fetchCookSession, recordCookTimerEvent, toggleCookIngredient, toggleCookStep } from "../api/recipes";
import { useWakeLock } from "../hooks/useWakeLock";
import { StepList } from "./StepList";
import { TimerAlertSettingsPanel } from "./TimerAlertSettings";
import { TimerBar } from "./TimerBar";
import { playTimerAlert } from "./timer-alerts";
import { loadTimerAlertSettings, remainingSeconds, useRecipeTimers, type ActiveTimer } from "./useRecipeTimers";

type CookModeProps = {
  recipeId: string;
  sessionId: string;
  onNavigate: (path: string) => void;
};

export function CookMode({ recipeId, sessionId, onNavigate }: CookModeProps): JSX.Element {
  const [recipe, setRecipe] = useState<Recipe | undefined>(undefined);
  const [session, setSession] = useState<CookSession | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [targetServings, setTargetServings] = useState<number | undefined>(undefined);
  const [alertSettings, setAlertSettings] = useState(loadTimerAlertSettings);
  const [headerFlash, setHeaderFlash] = useState(false);
  const wakeLock = useWakeLock(true);
  const {
    timers,
    nowMs,
    startTimer,
    pauseTimer,
    resumeTimer,
    cancelTimer,
    snoozeTimer,
    completedTimers,
    alertedRef
  } = useRecipeTimers(sessionId);
  const timerById = useRef(new Map<string, ActiveTimer>());

  useEffect(() => {
    const next = new Map<string, ActiveTimer>();
    for (const timer of timers) {
      next.set(timer.id, timer);
    }
    timerById.current = next;
  }, [timers]);

  const emitTimerEvent = useCallback(
    (action: "started" | "completed" | "cancelled", timer: ActiveTimer) => {
      void recordCookTimerEvent(sessionId, action, {
        timerId: timer.id,
        stepId: timer.stepId,
        label: timer.label,
        durationSeconds: timer.durationSeconds
      }).catch(() => {
        /* event log is best-effort while cooking */
      });
    },
    [sessionId]
  );

  const handleStartTimer = useCallback(
    (stepId: string, def: StepTimerDef) => {
      startTimer(stepId, def);
      void recordCookTimerEvent(sessionId, "started", {
        timerId: def.id,
        stepId,
        label: def.label,
        durationSeconds: def.durationSeconds
      }).catch(() => {
        /* best-effort */
      });
    },
    [sessionId, startTimer]
  );

  const handleCancelTimer = useCallback(
    (timerId: string) => {
      const timer = timerById.current.get(timerId);
      cancelTimer(timerId);
      if (timer !== undefined) {
        emitTimerEvent("cancelled", timer);
      }
    },
    [cancelTimer, emitTimerEvent]
  );

  useEffect(() => {
    for (const timer of completedTimers) {
      if (alertedRef.current.has(timer.id)) {
        continue;
      }
      alertedRef.current.add(timer.id);
      playTimerAlert(alertSettings);
      if (alertSettings.headerFlash) {
        setHeaderFlash(true);
      }
      emitTimerEvent("completed", timer);
    }
  }, [alertSettings, alertedRef, completedTimers, emitTimerEvent]);

  useEffect(() => {
    if (!headerFlash) {
      return;
    }
    const id = window.setTimeout(() => {
      setHeaderFlash(false);
    }, 2500);
    return () => {
      window.clearTimeout(id);
    };
  }, [headerFlash]);

  useEffect(() => {
    let active = true;
    void fetchCookSession(sessionId)
      .then((data) => {
        if (active) {
          setRecipe(data.recipe);
          setSession(data.session);
          setTargetServings(data.recipe.servings);
        }
      })
      .catch(() => {
        if (active) {
          setError("Cooking session not found.");
        }
      });
    return () => {
      active = false;
    };
  }, [sessionId]);

  async function onToggleIngredient(ingredientId: string): Promise<void> {
    try {
      const data = await toggleCookIngredient(sessionId, ingredientId);
      setRecipe(data.recipe);
      setSession(data.session);
    } catch {
      setError("Could not update ingredient.");
    }
  }

  async function onToggleStep(stepId: string): Promise<void> {
    try {
      const data = await toggleCookStep(sessionId, stepId);
      setRecipe(data.recipe);
      setSession(data.session);
    } catch {
      setError("Could not update step.");
    }
  }

  if (error !== undefined && recipe === undefined) {
    return (
      <div className="recipes-panel cook-mode">
        <p className="error-text" role="alert">{error}</p>
        <button className="secondary-action" type="button" onClick={() => { onNavigate(`/recipes/${recipeId}`); }}>
          Back to recipe
        </button>
      </div>
    );
  }

  if (recipe === undefined || session === undefined) {
    return <div className="recipes-panel cook-mode"><p className="muted-text">Loading cook mode…</p></div>;
  }

  const completedSteps = new Set(session.completedStepIds);
  const completedIngredients = new Set(session.completedIngredientIds);
  const servings = targetServings ?? recipe.servings;
  const scaleFactor = recipe.servings > 0 ? servings / recipe.servings : 1;
  const cookSteps = sortStepsForCookMode(recipe.steps);

  return (
    <div className={`recipes-panel cook-mode${headerFlash ? " cook-mode-alert" : ""}`}>
      <TimerBar
        timers={timers}
        nowMs={nowMs}
        flash={headerFlash}
        onSnooze={snoozeTimer}
        onCancel={handleCancelTimer}
      />
      <header className="recipe-detail-hero cook-header">
        <div className="recipe-detail-hero-content">
          <p className="eyebrow">Cooking</p>
          <h2 className="cook-title">{recipe.title}</h2>
          <div className="recipe-detail-meta" aria-label="Cooking details">
            <span>Serves {servings}</span>
          </div>
        </div>
        <div className="detail-actions recipe-detail-actions" aria-label="Cooking actions">
          <button className="secondary-action" type="button" onClick={() => { onNavigate(`/recipes/${recipeId}`); }}>
            Exit
          </button>
        </div>
      </header>

      {!wakeLock.supported ? (
        <p className="muted-text wake-lock-note">Screen may dim — wake lock is not supported on this browser.</p>
      ) : null}

      <TimerAlertSettingsPanel settings={alertSettings} onChange={setAlertSettings} />

      {error !== undefined ? <p className="error-text" role="alert">{error}</p> : null}

      <div className="recipe-detail-layout cook-detail-layout">
        <aside className="recipe-detail-sidebar" aria-label="Cooking preparation details">
          <section className="detail-section recipe-detail-card">
            <div className="detail-section-heading">
              <h3>Servings</h3>
            </div>
            <label className="field cook-servings">
              <span className="field-label">Servings</span>
              <input
                className="text-input"
                type="number"
                min={1}
                value={servings}
                onChange={(event) => {
                  const next = Number.parseInt(event.target.value, 10);
                  if (Number.isInteger(next) && next >= 1) {
                    setTargetServings(next);
                  }
                }}
              />
            </label>
          </section>

          {recipe.ingredients.length > 0 ? (
            <section className="detail-section recipe-detail-card">
              <div className="detail-section-heading">
                <h3>Ingredients</h3>
                <span>{recipe.ingredients.length}</span>
              </div>
              <ul className="ingredient-checklist recipe-detail-list">
                {recipe.ingredients.map((ingredient) => (
                  <li key={ingredient.id}>
                    <label className={`ingredient-check-item${completedIngredients.has(ingredient.id) ? " ingredient-check-item-done" : ""}`}>
                      <input
                        type="checkbox"
                        checked={completedIngredients.has(ingredient.id)}
                        onChange={() => {
                          void onToggleIngredient(ingredient.id);
                        }}
                      />
                      <span>{formatIngredientDisplay(ingredient, scaleFactor)}</span>
                    </label>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {recipe.equipment.length > 0 ? (
            <section className="detail-section recipe-detail-card recipe-equipment-card">
              <div className="detail-section-heading">
                <h3>Equipment</h3>
                <span>{recipe.equipment.length}</span>
              </div>
              <ul className="recipe-detail-list compact">
                {recipe.equipment.map((item) => <li key={item}>{item}</li>)}
              </ul>
            </section>
          ) : null}
        </aside>

        <section className="detail-section recipe-steps-section">
          <StepList
            steps={cookSteps}
            ingredients={recipe.ingredients}
            defaultAllExpanded={false}
            scaleFactor={scaleFactor}
            getStepDone={(stepId) => completedSteps.has(stepId)}
            onToggleStepDone={(stepId) => {
              void onToggleStep(stepId);
            }}
            getActiveTimer={(stepId, timerDefId) =>
              timers.find((timer) => timer.stepId === stepId && timer.id === timerDefId)
            }
            getTimerRemainingSeconds={(timer) => remainingSeconds(timer, nowMs)}
            onStartTimer={handleStartTimer}
            onPauseTimer={pauseTimer}
            onResumeTimer={resumeTimer}
            onCancelTimer={handleCancelTimer}
          />
        </section>
      </div>
    </div>
  );
}
